import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  listLeads,
  type LeadListParams,
  type LeadListResult,
  type ScanLead,
} from "../../../lib/scanLeads";
import { EMPTY_LEAD_FILTERS, type LeadFilterState } from "./leadFilters";
import { useScanAutopilot } from "./useScanAutopilot";

/**
 * The call queue's state: the filter bar and the table.
 *
 * The table follows the autopilot while a batch is running, because a
 * background job is the one thing that changes this list without anyone on
 * the page touching it. The engine itself - its status, the poll and when it
 * stops - lives in `useScanAutopilot.ts`, and its strip lives on the Skannid tab
 * now. This hook keeps only a busy read of it: it hands the engine the table's
 * reload, so rows appear as the run promotes them, and tells the page whether a
 * run is going so it can say why the list is moving.
 */

const SEARCH_DEBOUNCE_MS = 300;

export function useScanLeads() {
  const [filters, setFilters] = useState<LeadFilterState>(EMPTY_LEAD_FILTERS);
  const [qInput, setQInput] = useState("");
  const [sort, setSort] = useState<NonNullable<LeadListParams["sort"]>>("score");
  const [order, setOrder] = useState<"asc" | "desc">("desc");
  const [page, setPage] = useState(1);
  const pageSize = 25;

  const [data, setData] = useState<LeadListResult | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(false);

  const requestId = useRef(0);

  useEffect(() => {
    const timer = setTimeout(() => {
      setFilters((f) => (f.q === qInput ? f : { ...f, q: qInput }));
      setPage(1);
    }, SEARCH_DEBOUNCE_MS);
    return () => clearTimeout(timer);
  }, [qInput]);

  const params: LeadListParams = useMemo(
    () => ({
      q: filters.q || undefined,
      status: filters.status.length ? filters.status : undefined,
      severity: filters.severity.length ? filters.severity : undefined,
      hasEmail: filters.hasEmail || undefined,
      hasCompany: filters.hasCompany || undefined,
      sort,
      order,
      page,
      pageSize,
    }),
    [filters, sort, order, page],
  );

  const load = useCallback(async () => {
    const id = ++requestId.current;
    setLoading(true);
    try {
      const result = await listLeads(params);
      if (id !== requestId.current) return;
      setData(result);
      setError(false);
    } catch {
      if (id !== requestId.current) return;
      setError(true);
    } finally {
      if (id === requestId.current) setLoading(false);
    }
  }, [params]);

  useEffect(() => {
    void load();
  }, [load]);

  // The engine that fills this table. Its poll runs only while a batch is in
  // flight and pulls the table in with it; nothing else of it is read here.
  const autopilot = useScanAutopilot(load);
  const autopilotBusy = Boolean(autopilot.state?.busy);

  const patchFilters = useCallback((patch: Partial<LeadFilterState>) => {
    setFilters((f) => ({ ...f, ...patch }));
    setPage(1);
  }, []);

  const reset = useCallback(() => {
    setFilters(EMPTY_LEAD_FILTERS);
    setQInput("");
    setPage(1);
  }, []);

  const toggleSort = useCallback(
    (next: NonNullable<LeadListParams["sort"]>) => {
      if (next === sort) setOrder((o) => (o === "asc" ? "desc" : "asc"));
      else {
        setSort(next);
        setOrder(next === "company" ? "asc" : "desc");
      }
      setPage(1);
    },
    [sort],
  );

  /** Patch one row in place, so a status change does not reload the table. */
  const replaceLead = useCallback((lead: ScanLead) => {
    setData((d) =>
      d ? { ...d, items: d.items.map((item) => (item.domain === lead.domain ? lead : item)) } : d,
    );
  }, []);

  const removeLead = useCallback((domain: string) => {
    setData((d) =>
      d ? { ...d, items: d.items.filter((item) => item.domain !== domain), total: Math.max(0, d.total - 1) } : d,
    );
  }, []);

  return {
    filters,
    patchFilters,
    reset,
    qInput,
    setQInput,
    sort,
    order,
    toggleSort,
    page,
    setPage,
    pageSize,
    params,
    data,
    autopilotBusy,
    loading,
    error,
    refresh: load,
    replaceLead,
    removeLead,
  };
}
